import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * On every route change, snap scroll to the top. When a hash is present
 * (e.g. `/film#vernissage`), scroll to that anchor once the target has
 * rendered instead. Coordinated with Lenis (see components/SmoothScroll)
 * so native scrolls are not overwritten by the smooth-scroll RAF loop.
 */
export default function ScrollToTop() {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        const lenis = typeof window !== 'undefined' ? window.__lenis : null;

        const goToAnchor = () => {
            const el = document.getElementById(hash.slice(1));
            if (!el) return false;
            if (lenis) lenis.scrollTo(el, { immediate: true, force: true, offset: -120 });
            else el.scrollIntoView({ behavior: 'instant', block: 'start' });
            return true;
        };

        const goToTop = () => {
            if (lenis) lenis.scrollTo(0, { immediate: true, force: true });
            else window.scrollTo({ top: 0, behavior: 'instant' });
        };

        if (hash) {
            /* Wait one paint so the destination content has rendered */
            let tries = 0;
            const attempt = () => {
                if (goToAnchor()) return;
                if (++tries < 20) requestAnimationFrame(attempt);
                else goToTop();
            };
            requestAnimationFrame(attempt);
        } else {
            /* Reset both native + Lenis so nothing races us */
            goToTop();
            /* Second reset one tick later covers any late layout shift
               from images / fonts loading */
            const t = setTimeout(goToTop, 60);
            return () => clearTimeout(t);
        }
    }, [pathname, hash]);

    return null;
}
